import { Injectable } from '@angular/core';
import { FeatureCollection } from 'geojson';
import { environment } from '../../../environments/environment';

/**
 * Thin wrapper over the Mapbox Geocoding REST API. Plain `fetch`, no Mapbox
 * GL JS, no geocoder plugin, nothing that touches a `window` global.
 *
 * Split out of `SearchGeocoderService` deliberately. That service is
 * `providedIn: 'root'` and constructs a `MapboxGeocoder` and a
 * `mapboxgl.Marker`, so injecting it anywhere drags in 245 KB of CDN Mapbox.
 * `/sales-forecast/*` - where the paid traffic lands, and where no map exists
 * until the visitor picks an address - was injecting the whole thing to call
 * these two methods. See `todos/optimization.md` item 4.
 *
 * `SearchGeocoderService` delegates here, so there is still one
 * implementation of each call.
 */
@Injectable({
  providedIn: 'root',
})
export class GeocodingApiService {
  public async forwardGeocode(input: string): Promise<any> {
    // encodeURIComponent: `input` is whatever the visitor typed, and an
    // address containing & # + or ? would otherwise corrupt the query string.
    const url = `https://api.mapbox.com/search/geocode/v6/forward?q=${encodeURIComponent(
      input
    )}&autocomplete=${true}&country=US&access_token=${environment.mapbox.accessToken}`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

      return await response.json();
    } catch (error) {
      console.error('Forward geocoding failed:', error);
      throw error;
    }
  }

  public async reverseGeocode(coordinates: {
    lng: number;
    lat: number;
  }): Promise<FeatureCollection> {
    const url = `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${coordinates.lng}&latitude=${coordinates.lat}&access_token=${environment.mapbox.accessToken}`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

      return await response.json();
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      throw error;
    }
  }
}
